import type { Matrix2D, Transform, Vec2 } from '@/types';

export const IDENTITY_MATRIX: Matrix2D = Object.freeze({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 });

export const identityTransform = (): Transform => ({
  position: { x: 0, y: 0 },
  scale: { x: 1, y: 1 },
  rotation: 0,
});

export const transformAt = (position: Vec2): Transform => ({
  position,
  scale: { x: 1, y: 1 },
  rotation: 0,
});

/** Builds the affine matrix for a transform: translate · rotate · scale. */
export const matrixFromTransform = (t: Transform): Matrix2D => {
  const cos = Math.cos(t.rotation);
  const sin = Math.sin(t.rotation);
  return {
    a: cos * t.scale.x,
    b: sin * t.scale.x,
    c: -sin * t.scale.y,
    d: cos * t.scale.y,
    e: t.position.x,
    f: t.position.y,
  };
};

/** Returns `m · n` (apply `n` first, then `m`). */
export const multiply = (m: Matrix2D, n: Matrix2D): Matrix2D => ({
  a: m.a * n.a + m.c * n.b,
  b: m.b * n.a + m.d * n.b,
  c: m.a * n.c + m.c * n.d,
  d: m.b * n.c + m.d * n.d,
  e: m.a * n.e + m.c * n.f + m.e,
  f: m.b * n.e + m.d * n.f + m.f,
});

export const applyMatrix = (m: Matrix2D, p: Vec2): Vec2 => ({
  x: m.a * p.x + m.c * p.y + m.e,
  y: m.b * p.x + m.d * p.y + m.f,
});

export const invert = (m: Matrix2D): Matrix2D | null => {
  const det = m.a * m.d - m.b * m.c;
  if (det === 0 || !Number.isFinite(det)) return null;
  const inv = 1 / det;
  return {
    a: m.d * inv,
    b: -m.b * inv,
    c: -m.c * inv,
    d: m.a * inv,
    e: (m.c * m.f - m.d * m.e) * inv,
    f: (m.b * m.e - m.a * m.f) * inv,
  };
};

/** CSS `matrix()` string for use in `style.transform`. */
export const toCssMatrix = (m: Matrix2D): string => `matrix(${m.a}, ${m.b}, ${m.c}, ${m.d}, ${m.e}, ${m.f})`;

/** Inverse of `transformAt`: a transform with only translation is "plain". */
export const isPlainTransform = (t: Transform): boolean => t.rotation === 0 && t.scale.x === 1 && t.scale.y === 1;

/** Normalises radians into (−π, π]. */
export const normalizeAngle = (radians: number): number => {
  const tau = Math.PI * 2;
  let a = radians % tau;
  if (a > Math.PI) a -= tau;
  if (a <= -Math.PI) a += tau;
  return a;
};

export const degToRad = (deg: number): number => (deg * Math.PI) / 180;
export const radToDeg = (rad: number): number => (rad * 180) / Math.PI;

/**
 * Matrix mapping an object's local content box (0..width, 0..height) to its parent
 * frame. Rotation and scale are applied about the box centre, which is what users
 * expect from a rotate handle and what keeps resize math symmetric.
 */
export const objectMatrix = (t: Transform, size: { width: number; height: number }): Matrix2D => {
  const cx = size.width / 2;
  const cy = size.height / 2;
  const centre = { ...t, position: { x: t.position.x + cx, y: t.position.y + cy } };
  return multiply(matrixFromTransform(centre), { a: 1, b: 0, c: 0, d: 1, e: -cx, f: -cy });
};

/** The four corners of a content box after applying `m`, in order TL, TR, BR, BL. */
export const transformedCorners = (m: Matrix2D, size: { width: number; height: number }): [Vec2, Vec2, Vec2, Vec2] => [
  applyMatrix(m, { x: 0, y: 0 }),
  applyMatrix(m, { x: size.width, y: 0 }),
  applyMatrix(m, { x: size.width, y: size.height }),
  applyMatrix(m, { x: 0, y: size.height }),
];
